import React, { useCallback, useRef, useState } from "react";
import { Button, Row, Col, Alert } from "react-bootstrap";
import {
  MdCloudUpload,
  MdImage,
  MdDeleteOutline,
  MdCrop,
  MdInfoOutline,
  MdExpandMore,
  MdExpandLess,
} from "react-icons/md";

import MobileProductShootPicker from "./MobileProductShootPicker";
import AdditionalImagesStrip from "./AdditionalImagesStrip";
import { productPhotoHint } from "../utilities/productPhotoRules";

/**
 * ProductImageSection — the one product-image control, shared by the Create and
 * Edit product forms.
 *
 * WHY THIS EXISTS
 * The two forms had grown separate image blocks that behaved differently, and
 * both buried the ordinary path. A vendor who simply wanted to upload a photo
 * from their computer met a large gradient panel headlined "MOBILE PRODUCT
 * SHOOT" with three feature pills and a prominent dark "Shoot & pick image"
 * button, while the actual device upload was a 12px "Replace Image" text link.
 * The loudest control on the section was the one most people did not want.
 *
 * WHAT CHANGED
 *  - Device upload is now a real drop target: drag a file onto it, or click
 *    anywhere on it. Previously there was NO drag-and-drop anywhere in either
 *    form — only a hidden <input> behind a text link.
 *  - Shooting on a phone is a peer action beside it ("Shoot on phone"), same
 *    modal, same deep link, same live sync — just no longer the headline.
 *  - The five stacked lines of photo requirements collapse to one line plus a
 *    "Photo tips" toggle. They were taller than the image preview itself.
 *
 * DELIBERATELY PRESENTATIONAL. Each form keeps its own file handling — Edit
 * opens a crop modal on pick, Create runs its own dimension overlay — so this
 * emits a File and lets the parent decide. That keeps the shared component from
 * having to reconcile two different validation flows.
 */

const ACCEPT = ".jpg,.jpeg,.png";
const ACCEPT_MIME = ["image/jpeg", "image/png"];

const ProductImageSection = ({
  label = "Product image",
  image,
  /** (file: File) => void — a file arrived, by click or by drop */
  onFileSelected,
  /** () => void — clear the cover image */
  onRemoveImage,
  /** optional () => void — reopen the crop/preview modal for the current image */
  onEditImage,
  /** cover-image error text from the parent's validation */
  uploadError,

  additionalImages = [],
  onAdditionalImagesChange,
  /** Edit forms: (url) => void, opens the product photo delete dialog for a
      SAVED extra angle. See AdditionalImagesStrip. */
  onRequestDeleteSaved,

  vendorId,
  /** (url, item) => void — a shot picked from the phone-shoot modal */
  onShootImageSelected,

  disabled = false,
}) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [tipsOpen, setTipsOpen] = useState(false);
  const [dropError, setDropError] = useState("");

  const take = useCallback(
    (file) => {
      if (!file) return;
      // Guard here as well as in the parent: a drop can deliver anything at
      // all (a PDF, a folder, a screenshot in an unsupported format), and the
      // file dialog's `accept` filter does not apply to dropped files.
      if (!ACCEPT_MIME.includes(file.type)) {
        setDropError("That file isn't a JPG or PNG. Pick an image file.");
        return;
      }
      setDropError("");
      onFileSelected?.(file);
    },
    [onFileSelected]
  );

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragging(false);
      if (disabled) return;
      take(e.dataTransfer?.files?.[0]);
    },
    [disabled, take]
  );

  const openPicker = useCallback(() => {
    if (disabled) return;
    inputRef.current?.click();
  }, [disabled]);

  const zoneBorder = dragging ? "#0a578b" : image ? "#e2e8f0" : "#cbd5e1";

  return (
    <div className="mb-3">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
        <label className="form-label mb-0">{label}</label>
        <div className="d-flex align-items-center gap-2">
          {/* Peer to device upload, not a headline. See MobileProductShootPicker. */}
          <MobileProductShootPicker
            compact
            vendorId={vendorId}
            disabled={disabled}
            onSelectImage={onShootImageSelected}
          />
        </div>
      </div>

      <Row className="g-3">
        <Col md={7}>
          {/* ── Drop target / preview ─────────────────────────────────────── */}
          <div
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-label={image ? "Replace product image" : "Add product image"}
            onClick={image ? undefined : openPicker}
            onKeyDown={(e) => {
              if (image) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openPicker();
              }
            }}
            onDragOver={(e) => {
              e.preventDefault();
              if (!disabled) setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            style={{
              position: "relative",
              minHeight: 200,
              borderRadius: 12,
              border: `2px dashed ${zoneBorder}`,
              background: dragging ? "#f0f7ff" : image ? "#fff" : "#fafbfc",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: 14,
              cursor: image || disabled ? "default" : "pointer",
              transition: "border-color .15s, background .15s",
            }}
          >
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              disabled={disabled}
              onChange={(e) => {
                take(e.target.files?.[0]);
                // Reset so picking the SAME file twice still fires onChange —
                // otherwise a vendor who re-picks after a validation error
                // gets no response at all.
                e.target.value = "";
              }}
              style={{ display: "none" }}
            />

            {image ? (
              <img
                src={image}
                alt="Product"
                style={{ maxHeight: 200, maxWidth: "100%", objectFit: "contain" }}
              />
            ) : (
              <>
                <MdCloudUpload size={38} color={dragging ? "#0a578b" : "#94a3b8"} />
                <div
                  style={{
                    marginTop: 8,
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    color: "#334155",
                  }}
                >
                  Drag a photo here, or click to browse
                </div>
                <div style={{ fontSize: "0.76rem", color: "#94a3b8", marginTop: 3 }}>
                  {productPhotoHint()}
                </div>
              </>
            )}
          </div>

          {/* Actions live under the box so they read as operating on it. */}
          {image && (
            <div className="d-flex align-items-center gap-3 mt-2 flex-wrap">
              <Button
                variant="link"
                size="sm"
                className="p-0 text-decoration-none"
                onClick={openPicker}
                disabled={disabled}
                style={{ fontSize: "0.8rem", fontWeight: 600 }}
              >
                <MdImage size={15} style={{ marginRight: 5, marginTop: -2 }} />
                Replace
              </Button>
              {onEditImage && (
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 text-decoration-none"
                  onClick={onEditImage}
                  disabled={disabled}
                  style={{ fontSize: "0.8rem", fontWeight: 600 }}
                >
                  <MdCrop size={15} style={{ marginRight: 5, marginTop: -2 }} />
                  Crop
                </Button>
              )}
              <Button
                variant="link"
                size="sm"
                className="p-0 text-decoration-none text-danger"
                onClick={onRemoveImage}
                disabled={disabled}
                style={{ fontSize: "0.8rem", fontWeight: 600 }}
              >
                <MdDeleteOutline size={16} style={{ marginRight: 4, marginTop: -2 }} />
                Remove
              </Button>
            </div>
          )}

          {(dropError || uploadError) && (
            <Alert variant="warning" className="mt-2 mb-0 py-2 px-3" style={{ fontSize: "0.8rem" }}>
              {dropError || uploadError}
            </Alert>
          )}

          {/* ── Photo tips ────────────────────────────────────────────────────
              Was five stacked icon paragraphs, permanently on screen and taller
              than the preview. Same content, one line until asked for. */}
          <div className="mt-2">
            <Button
              variant="link"
              size="sm"
              className="p-0 text-decoration-none text-muted"
              onClick={() => setTipsOpen((v) => !v)}
              style={{ fontSize: "0.76rem" }}
            >
              <MdInfoOutline size={14} style={{ marginRight: 5, marginTop: -2 }} />
              Photo tips
              {tipsOpen ? (
                <MdExpandLess size={16} style={{ marginLeft: 2 }} />
              ) : (
                <MdExpandMore size={16} style={{ marginLeft: 2 }} />
              )}
            </Button>
            {tipsOpen && (
              <ul
                className="text-muted mt-1 mb-0"
                style={{ fontSize: "0.76rem", paddingLeft: 18, lineHeight: 1.7 }}
              >
                <li>Keep the product in focus and fully inside the frame.</li>
                <li>Centre it, on a clear background, in bright light.</li>
                <li>No added text, watermarks or price stickers.</li>
              </ul>
            )}
          </div>
        </Col>

        <Col md={5}>
          <AdditionalImagesStrip
            value={additionalImages}
            onChange={onAdditionalImagesChange}
            onRequestDeleteSaved={onRequestDeleteSaved}
            disabled={disabled}
          />
        </Col>
      </Row>
    </div>
  );
};

export default ProductImageSection;
